<template>
  <div class="bulk-order-gift-cards-preview">
    <div class="container-header">
      <PageHeadlineForBulkOrder
        class="gift-card-header"
        title="プレビュー"
        :show-stepper="false"
        :button-type="ButtonType.LINK"
        :button-label="giftCardForm.title"
      />
    </div>
    <div class="bulk-order-gift-cards-preview__content">
      <Preview
        :card-design-image-url="cardDesignImageUrl"
        :sender-name="giftCardForm.senderName"
        :profile-link="false"
        :message="giftCardForm.message"
        :item-sku-contents="giftCardForm.itemSkuContentAttributes"
      />
    </div>
    <form name="bulkOrderForm" method="post" :action="postUrl">
      <input type="hidden" name="authenticity_token" :value="csrfToken" />
      <input v-if="!isNewGiftCard" type="hidden" name="_method" value="PUT" />
      <input
        v-if="!isNewGiftCard"
        type="hidden"
        name="bulk_order_form[gift_card_token]"
        :value="giftCardSessionToken"
      />
      <input
        type="hidden"
        name="bulk_order_form[title]"
        :value="giftCardForm.title"
      />
      <input
        type="hidden"
        name="bulk_order_form[sender_name]"
        :value="giftCardForm.senderName"
      />
      <input
        type="hidden"
        name="bulk_order_form[message]"
        :value="giftCardForm.message"
      />
      <input
        type="hidden"
        name="bulk_order_form[publish_amount]"
        :value="giftCardForm.publishAmount"
      />
      <input
        v-if="giftCardForm.isCardDesign"
        type="hidden"
        name="bulk_order_form[card_design_id]"
        :value="giftCardForm.cardDesignId"
      />
      <input
        v-if="!giftCardForm.isCardDesign && !isChangePersonalCardDesign"
        type="hidden"
        name="bulk_order_form[personal_card_design_id]"
        :value="giftCardForm.personalCardDesignId"
      />
      <input
        v-if="!giftCardForm.isCardDesign && isChangePersonalCardDesign"
        type="hidden"
        name="bulk_order_form[personal_card_design_data_uri]"
        :value="giftCardForm.personalCardDesignDataUri"
      />
      <input
        v-if="!giftCardForm.isCardDesign && isChangePersonalCardDesign"
        type="hidden"
        name="bulk_order_form[personal_card_design_file_name]"
        :value="giftCardForm.personalCardDesignFileName"
      />
      <!-- eslint-disable vue/no-v-for-template-key v-forのときtemplateにkeyをつけれるが、lintエラーが発生するのでdisable -->
      <!-- eslint-disable prettier/prettier v-forの部分でvue/html-indentとの競合で警告が解決できなくなるのでdisableする -->
      <template
        v-for="(itemSkuContentAttribute,
                index) in giftCardForm.itemSkuContentAttributes"
        :key="index"
      >
        <input
          type="hidden"
          :name="
            `bulk_order_form[item_sku_content_attributes][${index}][item_sku_id]`
          "
          :value="itemSkuContentAttribute.itemSkuId"
        />
        <input
          type="hidden"
          :name="
            `bulk_order_form[item_sku_content_attributes][${index}][quantity]`
          "
          :value="itemSkuContentAttribute.quantity"
        />
      </template>
      <input type="submit" class="bulk-order-gift-cards-preview__submit" />
    </form>
    <div class="bulk-order-gift-cards-preview__bottom">
      <div class="bulk-order-gift-cards-preview__bottom--actions">
        <ButtonPrimary
          :text="submitButtonLabel"
          :disabled="(!validForm(giftCardSessionToken) || isSubmitButtonDisable) || null"
          @click-primary-button="onClickAddGiftCard"
        />
        <a class="text-link-to-top" :href="backGiftCardEditUrl">
          ギフトカードを編集する
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import PageHeadlineForBulkOrder, {
  ButtonType
} from '../../../organism/bulk_personal_orders/PageHeadlineForBulkOrder.vue'
import Preview from '../../../molecules/gift_card/Preview.vue'
import ButtonPrimary from '../../../molecules/buttons/ButtonPrimary.vue'
import { useBulkPersonalOrderSessionGiftCardStore } from '../../../../store/bulkPersonalOrderSessionGiftCard'
import { computed, onBeforeMount, ref } from 'vue'
import { bulkOrderAgreementChecker } from '../../../../pages/bulk_order/common/bulkOrderAgreementChecker'

const isSubmitButtonDisable = ref(false)

const props = defineProps({
  bulkPersonalOrderSessionId: {
    type: Number,
    required: true
  },
  giftCardSessionToken: {
    type: String,
    required: true
  }
})

onBeforeMount(() => {
  // まとめてオーダーの注意事項に同意していない場合は、ギフトカードリストへ戻す
  bulkOrderAgreementChecker()
})

const csrfToken = document
  .querySelector('meta[name="csrf-token"]')
  .getAttribute('content')

const store = useBulkPersonalOrderSessionGiftCardStore()
const { giftCardForms, validForm } = store

const giftCardForm = giftCardForms[props.giftCardSessionToken]

const isNewGiftCard = computed(() => props.giftCardSessionToken === 'new')
const isChangePersonalCardDesign = computed(
  () => giftCardForm.personalCardDesignId === ''
)
const cardDesignImageUrl = computed(() =>
  giftCardForm.isCardDesign
    ? giftCardForm.cardDesignImageUrl
    : giftCardForm.personalCardDesignDataUri
)

const postUrl = computed(() => {
  return isNewGiftCard.value
    ? '/bulk_order/gift_cards'
    : `/bulk_order/gift_cards/${props.giftCardSessionToken}`
})

const submitButtonLabel = computed(() => {
  return isNewGiftCard.value ? 'この内容でリストに追加する' : '内容を更新する'
})

const backGiftCardEditUrl = computed(() => {
  if (isNewGiftCard.value) {
    return '/bulk_order/gift_cards/new'
  }
  return `/bulk_order/gift_cards/${props.giftCardSessionToken}/edit`
})

const onClickAddGiftCard = () => {
  if (isNewGiftCard.value) {
    dataLayer.push({
      event: 'click_bulk_gift_card_preview__proceed',
      bulk_personal_order_session_id: props.bulkPersonalOrderSessionId
    })
  }

  isSubmitButtonDisable.value = true
  document.bulkOrderForm.submit()
}
</script>

<style lang="scss" scoped>
.bulk-order-gift-cards-preview {
  .container-header {
    display: flex;
    justify-content: center;
    background-color: $white;
    margin-bottom: 16px;
  }

  .gift-card-header {
    max-width: $small-contents-width;
    width: 100%;
  }

  &__content {
    margin: 0 auto;
    max-width: $small-contents-width;
  }

  &__bottom {
    max-width: $small-contents-width;
    margin: 0 auto;

    &--actions {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      gap: 32px;
      margin-top: 16px;
      padding: 40px 16px;
      background-color: $white;
    }
  }

  &__submit {
    display: none;
  }
}
</style>
